import { useState, useEffect } from "react";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Users, 
  UserPlus, 
  Mail, 
  Crown, 
  Shield, 
  Eye,
  Trash2,
  Check,
  X,
  Building2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const [team, setTeam] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", description: "" });
  const [inviteForm, setInviteForm] = useState({ email: "", role: "member" });

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      const [teamRes, invitationsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/teams/my-team`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/teams/invitations`, { withCredentials: true })
      ]);
      setTeam(teamRes.data);
      setInvitations(invitationsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch team data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) {
      toast.error("Team name is required");
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/teams`,
        createForm,
        { withCredentials: true }
      );
      setTeam(response.data);
      setShowCreateModal(false);
      toast.success("Team created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create team");
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    if (!inviteForm.email.trim()) {
      toast.error("Email is required");
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/teams/invite`,
        inviteForm,
        { withCredentials: true }
      );
      setShowInviteModal(false);
      setInviteForm({ email: "", role: "member" });
      toast.success(`Invitation sent to ${inviteForm.email}`);
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/teams/invitations/${invitationId}/accept`,
        {},
        { withCredentials: true }
      );
      toast.success("You've joined the team!");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept invitation");
    }
  };

  const handleDeclineInvitation = async (invitationId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/teams/invitations/${invitationId}/decline`,
        {},
        { withCredentials: true }
      );
      toast.success("Invitation declined");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline invitation");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/teams/members/${memberId}`,
        { withCredentials: true }
      );
      toast.success("Member removed");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/teams/members/${memberId}`,
        { role: newRole },
        { withCredentials: true }
      );
      toast.success("Member role updated");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "owner": return <Crown className="w-4 h-4 text-gold" />;
      case "admin": return <Shield className="w-4 h-4 text-amber-500" />;
      case "viewer": return <Eye className="w-4 h-4 text-white/70" />;
      default: return <Users className="w-4 h-4 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />;
    }
  };

  const isOwnerOrAdmin = team?.members?.some(
    m => m.user_id === user?.user_id && (m.role === "owner" || m.role === "admin")
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={`${mainClass} flex-1 p-8 transition-all duration-300`}>
          <div className="text-white/70">Loading...</div>
        </main>
      </div>
    );
  }

  // Check if user has Operator plan
  if (subscription?.tier !== "operator" && !team) {
    return (
      <div className="min-h-screen bg-black flex">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={`${mainClass} flex-1 p-8 transition-all duration-300`}>
          <div className="max-w-2xl mx-auto text-center py-20">
            <div className="w-20 h-20 bg-gold/10 border border-gold/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-gold" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase mb-4">
              Team Collaboration
            </h1>
            <p className="text-white/70 mb-8">
              Invite team members, share deals, and collaborate in real-time. 
              Available on the Operator plan.
            </p>
            <Button 
              onClick={() => window.location.href = "/upgrade"}
              className="gold-gradient-btn rounded-none font-bold"
            >
              Upgrade to Operator
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`${mainClass} flex-1 p-8 transition-all duration-300`}>
        <div className="max-w-4xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-white uppercase">
                Team
              </h1>
              <p className="text-white/70 mt-1">
                Manage your team and collaborate on deals
              </p>
            </div>
            
            {!team ? (
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="gold-gradient-btn rounded-none font-bold"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Create Team
              </Button>
            ) : isOwnerOrAdmin && (
              <Button 
                onClick={() => setShowInviteModal(true)}
                className="gold-gradient-btn rounded-none font-bold"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            )}
          </div>

          {/* Pending Invitations */}
          {invitations.length > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 p-6 mb-8">
              <h2 className="font-heading text-lg font-bold text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)] uppercase mb-4">
                Pending Invitations
              </h2>
              <div className="space-y-3">
                {invitations.map((inv) => (
                  <div 
                    key={inv.invitation_id}
                    className="flex items-center justify-between bg-black p-4 border border-white/[0.08]"
                  >
                    <div>
                      <p className="text-white font-medium">{inv.team_name}</p>
                      <p className="text-white/70 text-sm">Role: {inv.role}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleAcceptInvitation(inv.invitation_id)}
                        className="bg-emerald-600 hover:bg-emerald-700 rounded-none"
                      >
                        <Check className="w-4 h-4 mr-1" /> Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeclineInvitation(inv.invitation_id)}
                        className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
                      >
                        <X className="w-4 h-4 mr-1" /> Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Team Info */}
          {team ? (
            <>
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 mb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gold/10 border border-gold/30 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-gold" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-white">
                      {team.name}
                    </h2>
                    {team.description && (
                      <p className="text-white/70">{team.description}</p>
                    )}
                  </div>
                </div>
                <div className="text-white/70 text-sm">
                  {team.members?.length || 0} member{team.members?.length !== 1 ? "s" : ""}
                </div>
              </div>

              {/* Team Members */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08]">
                <div className="p-4 border-b border-white/[0.08]">
                  <h3 className="font-heading text-lg font-bold text-white uppercase">
                    Team Members
                  </h3>
                </div>
                <div className="divide-y divide-white/[0.06]">
                  {team.members?.map((member) => (
                    <div 
                      key={member.user_id}
                      className="p-4 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-white/[0.08] rounded-full flex items-center justify-center text-white font-bold">
                          {member.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{member.name}</p>
                            {getRoleIcon(member.role)}
                          </div>
                          <p className="text-white/70 text-sm">{member.email}</p>
                        </div>
                      </div>
                      
                      {isOwnerOrAdmin && member.role !== "owner" && member.user_id !== user?.user_id && (
                        <div className="flex items-center gap-3">
                          <Select
                            value={member.role}
                            onValueChange={(value) => handleUpdateRole(member.user_id, value)}
                          >
                            <SelectTrigger className="w-32 bg-black border-white/[0.08] text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                              <SelectItem value="admin" className="text-white hover:bg-white/[0.06]">Admin</SelectItem>
                              <SelectItem value="member" className="text-white hover:bg-white/[0.06]">Member</SelectItem>
                              <SelectItem value="viewer" className="text-white hover:bg-white/[0.06]">Viewer</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveMember(member.user_id)}
                            className="text-[#FF3B30] hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {member.role === "owner" && (
                        <span className="text-gold text-sm font-medium">Owner</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
              <Users className="w-16 h-16 text-white/70 mx-auto mb-4" />
              <h3 className="text-xl text-white font-medium mb-2">No Team Yet</h3>
              <p className="text-white/70 mb-6">
                Create a team to start collaborating with others
              </p>
              <Button 
                onClick={() => setShowCreateModal(true)}
                className="gold-gradient-btn rounded-none font-bold"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Create Team
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Create Team Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Create Team</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTeam} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-white/70">Team Name *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="DeHart Investment Group"
                className="bg-black border-white/[0.08] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Description</Label>
              <Input
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="Real estate investment team"
                className="bg-black border-white/[0.08] text-white"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
              >
                Cancel
              </Button>
              <Button type="submit" className="gold-gradient-btn rounded-none font-bold">
                Create Team
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Member Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Invite Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInviteMember} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-white/70">Email Address *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="teammate@example.com"
                className="bg-black border-white/[0.08] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger className="bg-black border-white/[0.08] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                  <SelectItem value="admin" className="text-white hover:bg-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-amber-500" />
                      Admin - Can manage team and deals
                    </div>
                  </SelectItem>
                  <SelectItem value="member" className="text-white hover:bg-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]" />
                      Member - Can view and edit deals
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer" className="text-white hover:bg-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <Eye className="w-4 h-4 text-white/70" />
                      Viewer - Can only view deals
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowInviteModal(false)}
                className="border-white/[0.1] text-white/70 hover:bg-white/[0.06] rounded-none"
              >
                Cancel
              </Button>
              <Button type="submit" className="gold-gradient-btn rounded-none font-bold">
                <Mail className="w-4 h-4 mr-2" />
                Send Invitation
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
